import React, { useState, useRef, useEffect } from 'react';
import { TextField, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormContainer from '../../components/FormContainer';
import { addProfession, updateProfession } from '../../utils/api';

const ProfessionForm = ({ editProfession = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const isEditMode = !!editProfession;
  const onSuccessTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (onSuccessTimerRef.current != null) {
        clearTimeout(onSuccessTimerRef.current);
        onSuccessTimerRef.current = null;
      }
    },
    []
  );

  const validationSchema = Yup.object({
    professionName: Yup.string().required('Profession name is required').max(100, 'Profession name must be at most 100 characters'),
    description: Yup.string().required('Description is required').max(500, 'Description must be at most 500 characters'),
  });

  const formik = useFormik({
    initialValues: {
      professionName: editProfession ? editProfession.professionName : '',
      description: editProfession ? editProfession.description : '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        if (isEditMode) {
          await updateProfession(editProfession.professionId, values, {
            previousData: editProfession,
            identifier: editProfession.professionName || values.professionName
          });
          setSuccessMessage('Profession updated successfully!');
        } else {
          await addProfession(values, {
            identifier: values.professionName
          });
          setSuccessMessage('Profession added successfully!');
          resetForm();
        }

        if (onSuccess) {
          if (onSuccessTimerRef.current != null) clearTimeout(onSuccessTimerRef.current);
          onSuccessTimerRef.current = setTimeout(() => {
            onSuccessTimerRef.current = null;
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'adding'} profession:`, error);
        setErrorMessage(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} profession. Please try again.`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  return (
    <FormContainer
      title={isEditMode ? "Edit Profession" : "Add Profession"}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={errorMessage}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="professionName"
            name="professionName"
            label="Profession Name"
            value={formik.values.professionName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.professionName && Boolean(formik.errors.professionName)}
            helperText={formik.touched.professionName && formik.errors.professionName}
            margin="normal"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            name="description"
            label="Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            margin="normal"
            variant="outlined"
            multiline
            rows={4}
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default ProfessionForm; 